import { sql } from "drizzle-orm";
import type {
  DocumentConvertResult,
  DocumentVisibility,
  RagIngestJobRecord,
  RagIngestJobStatus,
} from "@gencow/core";
import { GENCOW_APP_WAKE_CHANNEL, buildWakeSignalPayload } from "./wake-signal/wake-signal-types.js";

const RAG_INGEST_WAKE_SIGNAL_PAYLOAD = buildWakeSignalPayload("rag_ingest");

type SqlDbLike = {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
  transaction?: <T>(run: (tx: SqlDbLike) => Promise<T>) => Promise<T>;
};

type RagSourceRow = {
  id: string;
  corpus: string;
  visibility_scope: DocumentVisibility;
  owner_user_id: string | null;
  storage_id: string;
  source_key: string;
  source_title: string;
  mime_type: string;
  byte_size: number;
  source_checksum: string;
  convert_provider: string;
  convert_status: string;
  markdown: string;
  text: string;
  warnings: unknown;
  metadata: unknown;
  created_at: string | Date;
  updated_at: string | Date;
};

type RagIngestJobRow = {
  id: string;
  workflow_id: string;
  source_id: string;
  corpus: string;
  visibility_scope: DocumentVisibility;
  owner_user_id: string | null;
  status: string;
  stage: string;
  provider_trace: unknown;
  metrics: unknown;
  error: string | null;
  started_at: string | Date;
  completed_at: string | Date | null;
  workflow_status: string | null;
  workflow_error: string | null;
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function parseJsonField<T>(value: unknown, fallback: T): T {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  if (typeof value === "object") {
    return value as T;
  }
  return fallback;
}

function toIsoString(value: string | Date): string {
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : String(value);
}

function toOptionalIsoString(value: string | Date | null): string | null {
  return value ? toIsoString(value) : null;
}

function matchesOwner(viewerUserId: string, ownerUserId: string | null): boolean {
  return ownerUserId == null || ownerUserId === viewerUserId;
}

function normalizeJobStatus(row: RagIngestJobRow): RagIngestJobStatus {
  if (row.workflow_status === "completed") return "completed";
  if (row.workflow_status === "failed") {
    return row.workflow_error === "Cancelled by user" ? "canceled" : "failed";
  }
  if (row.status === "completed" || row.status === "failed" || row.status === "canceled") {
    return row.status;
  }
  return (row.status || "queued") as RagIngestJobStatus;
}

function mapJobRow(row: RagIngestJobRow): RagIngestJobRecord {
  const status = normalizeJobStatus(row);
  return {
    id: row.id,
    workflowId: row.workflow_id,
    sourceId: row.source_id,
    corpus: row.corpus,
    visibility: row.visibility_scope,
    ownerUserId: row.owner_user_id,
    status,
    stage:
      status === "completed"
        ? "completed"
        : status === "failed"
          ? "failed"
          : status === "canceled"
            ? "canceled"
            : row.stage,
    providerTrace: parseJsonField(row.provider_trace, {}),
    metrics: parseJsonField(row.metrics, {}),
    error: row.workflow_error ?? row.error,
    startedAt: toIsoString(row.started_at),
    completedAt: toOptionalIsoString(row.completed_at),
  };
}

export async function findRagSourceByScopeAndKey(
  db: SqlDbLike,
  params: {
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
    sourceKey: string;
  },
): Promise<RagSourceRow | null> {
  const ownerFilter =
    params.ownerUserId == null
      ? sql`AND owner_user_id IS NULL`
      : sql`AND owner_user_id = ${params.ownerUserId}`;
  const result = await db.execute(sql`
        SELECT *
        FROM rag_sources
        WHERE corpus = ${params.corpus}
          AND visibility_scope = ${params.visibility}
          AND source_key = ${params.sourceKey}
          ${ownerFilter}
        LIMIT 1
    `);
  return rowsFromResult<RagSourceRow>(result)[0] ?? null;
}

export async function loadAccessibleRagSource(
  db: SqlDbLike,
  params: {
    sourceId: string;
    viewerUserId: string;
  },
): Promise<RagSourceRow | null> {
  const result = await db.execute(sql`
        SELECT *
        FROM rag_sources
        WHERE id = ${params.sourceId}
        LIMIT 1
    `);
  const row = rowsFromResult<RagSourceRow>(result)[0] ?? null;
  if (!row || !matchesOwner(params.viewerUserId, row.owner_user_id)) {
    return null;
  }
  return row;
}

export async function findLatestRagIngestJobBySourceId(
  db: SqlDbLike,
  sourceId: string,
): Promise<RagIngestJobRecord | null> {
  const result = await db.execute(sql`
        SELECT
          j.*,
          w.status AS workflow_status,
          w.error AS workflow_error
        FROM rag_ingest_jobs j
        LEFT JOIN _gencow_workflows w ON w.id = j.workflow_id
        WHERE j.source_id = ${sourceId}
        ORDER BY j.started_at DESC
        LIMIT 1
    `);
  const row = rowsFromResult<RagIngestJobRow>(result)[0] ?? null;
  return row ? mapJobRow(row) : null;
}

export async function upsertQueuedRagSource(
  db: SqlDbLike,
  params: {
    sourceId: string;
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
    storageId: string;
    sourceKey: string;
    sourceTitle: string;
    mimeType: string;
    byteSize: number;
    checksum: string;
    provider: string;
    metadata: Record<string, unknown>;
  },
): Promise<void> {
  await db.execute(sql`
        INSERT INTO rag_sources (
          id,
          corpus,
          visibility_scope,
          owner_user_id,
          storage_id,
          source_key,
          source_title,
          mime_type,
          byte_size,
          source_checksum,
          convert_provider,
          convert_status,
          markdown,
          text,
          warnings,
          metadata
        )
        VALUES (
          ${params.sourceId},
          ${params.corpus},
          ${params.visibility},
          ${params.ownerUserId},
          ${params.storageId},
          ${params.sourceKey},
          ${params.sourceTitle},
          ${params.mimeType},
          ${params.byteSize},
          ${params.checksum},
          ${params.provider},
          'queued',
          '',
          '',
          ${JSON.stringify([])}::jsonb,
          ${JSON.stringify(params.metadata)}::jsonb
        )
        ON CONFLICT (id)
        DO UPDATE SET
          corpus = EXCLUDED.corpus,
          visibility_scope = EXCLUDED.visibility_scope,
          owner_user_id = EXCLUDED.owner_user_id,
          storage_id = EXCLUDED.storage_id,
          source_key = EXCLUDED.source_key,
          source_title = EXCLUDED.source_title,
          mime_type = EXCLUDED.mime_type,
          byte_size = EXCLUDED.byte_size,
          source_checksum = EXCLUDED.source_checksum,
          convert_provider = EXCLUDED.convert_provider,
          convert_status = 'queued',
          warnings = '[]'::jsonb,
          metadata = EXCLUDED.metadata,
          updated_at = NOW()
    `);
}

export async function upsertRagCorpus(
  db: SqlDbLike,
  params: {
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
    allowExternalProviders: boolean;
    metadata: Record<string, unknown>;
  },
): Promise<void> {
  const corpusId = `${params.corpus}:${params.visibility}:${params.ownerUserId ?? "shared"}`;
  await db.execute(sql`
        INSERT INTO rag_corpora (
          id,
          corpus,
          visibility_scope,
          owner_user_id,
          title,
          allow_external_providers,
          metadata
        )
        VALUES (
          ${corpusId},
          ${params.corpus},
          ${params.visibility},
          ${params.ownerUserId},
          ${params.corpus},
          ${params.allowExternalProviders},
          ${JSON.stringify(params.metadata)}::jsonb
        )
        ON CONFLICT (id)
        DO UPDATE SET
          allow_external_providers = EXCLUDED.allow_external_providers,
          metadata = EXCLUDED.metadata,
          updated_at = NOW()
    `);
}

export async function createQueuedRagIngestJob(
  db: SqlDbLike,
  params: {
    jobId: string;
    workflowId: string;
    sourceId: string;
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
  },
): Promise<void> {
  await db.execute(sql`
      WITH inserted AS (
        INSERT INTO rag_ingest_jobs (
          id,
          corpus,
          visibility_scope,
          owner_user_id,
          workflow_id,
          source_id,
          status,
          stage,
          provider_trace,
          metrics
        )
        VALUES (
          ${params.jobId},
          ${params.corpus},
          ${params.visibility},
          ${params.ownerUserId},
          ${params.workflowId},
          ${params.sourceId},
          'queued',
          'queued',
          '{}'::jsonb,
          '{}'::jsonb
        )
        RETURNING id
      ),
      wake_notified AS (
        SELECT pg_notify(${GENCOW_APP_WAKE_CHANNEL}, ${RAG_INGEST_WAKE_SIGNAL_PAYLOAD})
        FROM inserted
      )
      SELECT inserted.id
      FROM inserted
      LEFT JOIN wake_notified ON TRUE
    `);
}

export async function setRagIngestJobStage(
  db: SqlDbLike,
  params: {
    jobId: string;
    status: RagIngestJobStatus;
    stage: string;
    metrics?: Record<string, unknown>;
    providerTrace?: Record<string, unknown>;
  },
): Promise<void> {
  const metricsValue = params.metrics ? sql`${JSON.stringify(params.metrics)}::jsonb` : sql`metrics`;
  const providerTraceValue = params.providerTrace
    ? sql`${JSON.stringify(params.providerTrace)}::jsonb`
    : sql`provider_trace`;

  await db.execute(sql`
        UPDATE rag_ingest_jobs
        SET
          status = ${params.status},
          stage = ${params.stage},
          metrics = ${metricsValue},
          provider_trace = ${providerTraceValue},
          updated_at = NOW()
        WHERE id = ${params.jobId}
    `);
}

export async function resetRagIngestJobForRetry(
  db: SqlDbLike,
  params: {
    jobId: string;
    metrics?: Record<string, unknown>;
    providerTrace?: Record<string, unknown>;
  },
): Promise<void> {
  await db.execute(sql`
      WITH updated AS (
        UPDATE rag_ingest_jobs
        SET
          status = 'queued',
          stage = 'queued',
          metrics = ${JSON.stringify(params.metrics ?? {})}::jsonb,
          provider_trace = ${JSON.stringify(params.providerTrace ?? {})}::jsonb,
          error = NULL,
          completed_at = NULL,
          updated_at = NOW()
        WHERE id = ${params.jobId}
        RETURNING id
      ),
      wake_notified AS (
        SELECT pg_notify(${GENCOW_APP_WAKE_CHANNEL}, ${RAG_INGEST_WAKE_SIGNAL_PAYLOAD})
        FROM updated
      )
      SELECT updated.id
      FROM updated
      LEFT JOIN wake_notified ON TRUE
    `);
}

export async function finalizeRagIngestJob(
  db: SqlDbLike,
  params: {
    jobId: string;
    status: "completed" | "failed" | "canceled";
    error?: string | null;
    metrics?: Record<string, unknown>;
    providerTrace?: Record<string, unknown>;
  },
): Promise<void> {
  await db.execute(sql`
        UPDATE rag_ingest_jobs
        SET
          status = ${params.status},
          stage = ${params.status},
          error = ${params.error ?? null},
          metrics = ${JSON.stringify(params.metrics ?? {})}::jsonb,
          provider_trace = ${JSON.stringify(params.providerTrace ?? {})}::jsonb,
          completed_at = NOW(),
          updated_at = NOW()
        WHERE id = ${params.jobId}
    `);
}

export async function persistConvertedRagSource(
  db: SqlDbLike,
  params: {
    sourceId: string;
    provider: string;
    result: DocumentConvertResult;
    metadata: Record<string, unknown>;
  },
): Promise<void> {
  await db.execute(sql`
        UPDATE rag_sources
        SET
          convert_provider = ${params.provider},
          convert_status = 'ready',
          markdown = ${params.result.markdown},
          text = ${params.result.text},
          warnings = ${JSON.stringify(params.result.warnings)}::jsonb,
          metadata = ${JSON.stringify(params.metadata)}::jsonb,
          updated_at = NOW()
        WHERE id = ${params.sourceId}
    `);
}

export async function replaceRagSectionsAndChunks(
  db: SqlDbLike,
  params: {
    sourceId: string;
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
    sections: Array<{
      id: string;
      title?: string;
      depth: number;
      path: string[];
      pageStart?: number;
      pageEnd?: number;
      charStart: number;
      charEnd: number;
    }>;
    chunks: Array<{
      id: string;
      sectionId: string | null;
      chunkIndex: number;
      chunkText: string;
      lexicalText: string;
      embedding: number[] | null;
      pageStart: number | null;
      pageEnd: number | null;
      chunkChecksum: string;
      metadata: Record<string, unknown>;
    }>;
  },
): Promise<void> {
  const run = async (tx: SqlDbLike) => {
    await tx.execute(sql`DELETE FROM rag_chunks WHERE source_id = ${params.sourceId}`);
    await tx.execute(sql`DELETE FROM rag_sections WHERE source_id = ${params.sourceId}`);

    for (let index = 0; index < params.sections.length; index += 1) {
      const section = params.sections[index];
      await tx.execute(sql`
            INSERT INTO rag_sections (
              id,
              corpus,
              visibility_scope,
              owner_user_id,
              source_id,
              section_index,
              section_path,
              title,
              depth,
              page_start,
              page_end,
              char_start,
              char_end
            )
            VALUES (
              ${section.id},
              ${params.corpus},
              ${params.visibility},
              ${params.ownerUserId},
              ${params.sourceId},
              ${index},
              ${JSON.stringify(section.path)}::jsonb,
              ${section.title ?? null},
              ${section.depth},
              ${section.pageStart ?? null},
              ${section.pageEnd ?? null},
              ${section.charStart},
              ${section.charEnd}
            )
        `);
    }

    for (const chunk of params.chunks) {
      await tx.execute(sql`
            INSERT INTO rag_chunks (
              id,
              corpus,
              visibility_scope,
              owner_user_id,
              source_id,
              section_id,
              chunk_index,
              chunk_text,
              lexical_text,
              embedding,
              page_start,
              page_end,
              chunk_checksum,
              metadata
            )
            VALUES (
              ${chunk.id},
              ${params.corpus},
              ${params.visibility},
              ${params.ownerUserId},
              ${params.sourceId},
              ${chunk.sectionId},
              ${chunk.chunkIndex},
              ${chunk.chunkText},
              ${chunk.lexicalText},
              ${chunk.embedding == null ? null : sql`${JSON.stringify(chunk.embedding)}::vector`},
              ${chunk.pageStart},
              ${chunk.pageEnd},
              ${chunk.chunkChecksum},
              ${JSON.stringify(chunk.metadata)}::jsonb
            )
        `);
    }
  };

  if (typeof db.transaction === "function") {
    await db.transaction(run);
    return;
  }
  await run(db);
}

export async function markRagSourceFailed(
  db: SqlDbLike,
  params: {
    sourceId: string;
    provider: string;
    error: string;
  },
): Promise<void> {
  await db.execute(sql`
        UPDATE rag_sources
        SET
          convert_provider = ${params.provider},
          convert_status = 'failed',
          warnings = ${JSON.stringify([params.error])}::jsonb,
          updated_at = NOW()
        WHERE id = ${params.sourceId}
    `);
}

export async function loadAccessibleRagIngestJob(
  db: SqlDbLike,
  params: {
    jobId: string;
    viewerUserId: string;
  },
): Promise<RagIngestJobRecord | null> {
  const result = await db.execute(sql`
        SELECT
          j.*,
          w.status AS workflow_status,
          w.error AS workflow_error
        FROM rag_ingest_jobs j
        LEFT JOIN _gencow_workflows w ON w.id = j.workflow_id
        WHERE j.id = ${params.jobId}
        LIMIT 1
    `);
  const row = rowsFromResult<RagIngestJobRow>(result)[0] ?? null;
  if (!row || !matchesOwner(params.viewerUserId, row.owner_user_id)) {
    return null;
  }
  return mapJobRow(row);
}

export async function listAccessibleRagIngestJobs(
  db: SqlDbLike,
  params: {
    viewerUserId: string;
    limit: number;
    status?: RagIngestJobStatus;
    corpus?: string;
  },
): Promise<RagIngestJobRecord[]> {
  const statusFilter = params.status ? sql`AND j.status = ${params.status}` : sql``;
  const corpusFilter = params.corpus ? sql`AND j.corpus = ${params.corpus}` : sql``;

  const result = await db.execute(sql`
        SELECT
          j.*,
          w.status AS workflow_status,
          w.error AS workflow_error
        FROM rag_ingest_jobs j
        LEFT JOIN _gencow_workflows w ON w.id = j.workflow_id
        WHERE (j.owner_user_id IS NULL OR j.owner_user_id = ${params.viewerUserId})
          ${statusFilter}
          ${corpusFilter}
        ORDER BY j.started_at DESC
        LIMIT ${params.limit}
    `);
  return rowsFromResult<RagIngestJobRow>(result).map(mapJobRow);
}
